import { useEffect, useState } from 'react';

import { MapboxOverlay as DeckOverlay } from '@deck.gl/mapbox';
import type { MapboxOverlayProps } from '@deck.gl/mapbox';
import { useWindowSize } from '@uidotdev/usehooks';
import { scaleLinear, scaleThreshold } from 'd3-scale';
import { ArcLayer, Color, LayersList, PickingInfo } from 'deck.gl';
import { groupBy, maxBy, mean, minBy } from 'lodash';
import { ProjectionSpecification } from 'mapbox-gl';
import { Map, NavigationControl, ScaleControl, useControl } from 'react-map-gl';
import { useSnapshot } from 'valtio';

import { ThemeProvider, useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

import dayjs from '@/dayjs';

import { denseTheme, desktopTheme } from '@/theme';

import {
  getBookingsExpandedQueryWhereClauseFromFilters,
  store,
} from '@/components/pages/EmissionsDashboard/store';

import { useGetBookingsExpandedWithLegsQuery } from '@/graphql/GetBookingsExpandedWithLegs.generated';
import { Container_Insights_Bookings_Expanded } from '@/graphql/types';

import { useBreakpoints } from '@/hooks';

import { appStore, reportRequestError } from '@/store';

import { FiltersPanel } from './Filters';
import { MapTooltip } from './MapTooltip';
import { TripList } from './TripList';
import './mapbox-overrides.css';

import 'mapbox-gl/dist/mapbox-gl.css';

const {
  VITE_MAPBOX_TOKEN,
  VITE_MAPBOX_STYLE,
  VITE_MAPBOX_PROJECTION,
  VITE_MAPBOX_DEFAULT_ZOOM,
  VITE_MAPBOX_DEFAULT_LATITUDE,
  VITE_MAPBOX_DEFAULT_LONGITUDE,
} = import.meta.env;

export const colorRange: Color[] = [
  [1, 152, 189],
  [73, 227, 206],
  [216, 254, 181],
  [254, 237, 177],
  [254, 173, 84],
  [209, 55, 78],
];

export type BookingWithDailyAverageCo2e =
  Container_Insights_Bookings_Expanded & {
    co2eq_kg_daily_average: number;
  };

interface BookingGroup {
  id: string;
  start_long_location: string;
  start_iso: string;
  end_long_location: string;
  end_iso: string;
  start_gps: any;
  end_gps: any;
  bookings: BookingWithDailyAverageCo2e[];
  // co2eq_kg: number;
  co2eq_kg_daily_average: number;
}

function DeckGLOverlay(props: MapboxOverlayProps) {
  const overlay = useControl(() => new DeckOverlay(props) as any);
  overlay.setProps(props);
  return null;
}

// Gray color for N/A
const naRgbColor: Color = [110, 110, 110];

function ManageCo2e() {
  const theme = useTheme();
  const { isMobileDevice } = useBreakpoints();
  const windowSize = useWindowSize();
  const appSnap = useSnapshot(appStore);

  const snap = useSnapshot(store) as typeof store;

  const where = getBookingsExpandedQueryWhereClauseFromFilters(
    snap as typeof store,
  );

  const [{ data, error, fetching }, reexecuteQuery] =
    useGetBookingsExpandedWithLegsQuery({
      variables: {
        where,
      },
    });

  useEffect(() => {
    if (error) {
      reportRequestError(error, reexecuteQuery);
    }
  }, [error]);

  const width = windowSize.width || 0;
  const height = windowSize.height
    ? windowSize.height - appSnap.appBar.height
    : 0;

  const bookings = data ? data.container_insights_bookings_expanded : [];

  const [popupInfo, setPopupInfo] = useState<PickingInfo | null>(null);
  const [holdPopup, setHoldPopup] = useState(false);

  const groupedByStartEnd = groupBy(
    bookings,
    (b: Container_Insights_Bookings_Expanded) =>
      `${b.start_location}-${b.end_location}`,
  ) as { [key: string]: Container_Insights_Bookings_Expanded[] };

  const groupedBookings: BookingGroup[] = Object.entries(groupedByStartEnd).map(
    ([key, values]) => {
      const {
        start_gps,
        end_gps,
        start_long_location,
        end_long_location,
        start_iso,
        end_iso,
      } = values[0];

      const bookings: BookingWithDailyAverageCo2e[] = values.map((booking) => {
        let co2eq_kg_daily_average = 0;
        if (booking.co2eq_kg) {
          const start = dayjs(booking.start_datetime);
          const end = dayjs(booking.end_datetime);
          const diffDays = end.diff(start, 'days', true);
          const diffHours = end.diff(start, 'hours', true);
          if (diffDays || diffHours) {
            co2eq_kg_daily_average =
              diffDays > 0
                ? booking.co2eq_kg / diffDays
                : booking.co2eq_kg * (diffHours / 24);
          }
        }
        return {
          ...booking,
          co2eq_kg_daily_average,
        };
      });

      const average = mean(
        bookings
          .filter((booking) => booking.co2eq_kg_daily_average > 0)
          .map((booking) => booking.co2eq_kg_daily_average),
      );

      const co2eq_kg_daily_average = !isNaN(average) ? average : 0;

      return {
        id: key,
        start_long_location: start_long_location as string,
        end_long_location: end_long_location as string,
        start_gps,
        end_gps,
        start_iso: start_iso as string,
        end_iso: end_iso as string,
        bookings,
        co2eq_kg_daily_average,
      };
    },
  );

  const minCo2GroupedTrips = minBy(groupedBookings, 'co2eq_kg_daily_average');
  const maxCo2GroupedTrips = maxBy(groupedBookings, 'co2eq_kg_daily_average');

  const minCo2GroupedTripsValue =
    minCo2GroupedTrips?.co2eq_kg_daily_average || 0;
  const maxCo2GroupedTripsValue =
    maxCo2GroupedTrips?.co2eq_kg_daily_average || 0;

  const co2GroupedTripsDomain = [...Array(10).keys()].map((value) =>
    Math.round(
      minCo2GroupedTripsValue +
        ((maxCo2GroupedTripsValue - minCo2GroupedTripsValue) * value) / 9,
    ),
  );

  const groupedTripsColorScale = scaleThreshold<number, Color>()
    .domain(co2GroupedTripsDomain)
    .range([
      [26, 152, 80],
      [102, 189, 99],
      [166, 217, 106],
      [217, 239, 139],
      [255, 255, 191],
      [254, 224, 139],
      [253, 174, 97],
      [244, 109, 67],
      [215, 48, 39],
      [168, 0, 0],
    ]);

  const minGroupedBookingsCount = minBy(groupedBookings, 'bookings.length');
  const maxGroupedBookingsCount = maxBy(groupedBookings, 'bookings.length');

  const minGroupedBookingsCountValue =
    minGroupedBookingsCount?.bookings.length || 0;
  const maxGroupedBookingsCountValue =
    maxGroupedBookingsCount?.bookings.length || 0;

  const countGroupedBookingsDomain = [...Array(10).keys()].map((value) =>
    Math.round(
      minGroupedBookingsCountValue +
        ((maxGroupedBookingsCountValue - minGroupedBookingsCountValue) *
          value) /
          9,
    ),
  );

  const groupedTripsWidthScale = scaleLinear()
    .clamp(true)
    .domain(countGroupedBookingsDomain)
    .range([1, 2, 2, 4, 4, 4, 6, 6, 6, 8]);

  function getGroupedTripsColor(d: BookingWithDailyAverageCo2e): Color {
    if (!d || !d.co2eq_kg_daily_average) {
      return naRgbColor;
    }
    return groupedTripsColorScale(d.co2eq_kg_daily_average as number);
  }

  const layers: LayersList = [
    new ArcLayer({
      id: 'ArcLayer',
      data: groupedBookings,
      greatCircle: false,
      numSegments: 100,
      // widthScale,
      opacity: 0.3,
      getSourcePosition: (d) => d.start_gps.coordinates,
      getTargetPosition: (d) => d.end_gps.coordinates,
      getSourceColor: getGroupedTripsColor,
      getTargetColor: getGroupedTripsColor,
      getWidth: (d) => groupedTripsWidthScale(d.bookings.length),
      pickable: true,
      onHover: (info) => {
        if (!holdPopup) {
          setPopupInfo(info);
        }
      },
      onClick: () => {
        setHoldPopup(!holdPopup);
      },
    }),
  ];

  return (
    <ThemeProvider theme={isMobileDevice ? denseTheme : desktopTheme}>
      <Box>
        {fetching && <LinearProgress />}
        <Map
          mapboxAccessToken={VITE_MAPBOX_TOKEN}
          mapStyle={VITE_MAPBOX_STYLE}
          projection={{
            name: VITE_MAPBOX_PROJECTION as ProjectionSpecification['name'],
          }}
          initialViewState={{
            longitude: parseFloat(VITE_MAPBOX_DEFAULT_LONGITUDE),
            latitude: parseFloat(VITE_MAPBOX_DEFAULT_LATITUDE),
            zoom: parseInt(VITE_MAPBOX_DEFAULT_ZOOM),
          }}
          reuseMaps
          style={{
            width,
            height,
          }}
          pitch={30}
        >
          <DeckGLOverlay
            layers={layers}
            interleaved={true}
            pickingRadius={8}
          ></DeckGLOverlay>

          {popupInfo && (
            <MapTooltip
              popup={popupInfo}
              colorScale={groupedTripsColorScale}
              elevation={holdPopup ? 8 : 1}
              onClose={() => {
                setPopupInfo(null);
                setHoldPopup(false);
              }}
            />
          )}

          {/* <FullscreenControl position="top-right" /> */}
          <NavigationControl position="top-right" />
          <ScaleControl position="bottom-right" />

          <FiltersPanel />
          <TripList />
        </Map>
      </Box>
    </ThemeProvider>
  );
}

export { ManageCo2e };
